import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getBeamKPIs, BeamError } from '../apis/beam/api';
import { BEAM_ERROR_MESSAGES } from '../common/utils/constants';
import { Status } from '../common/utils/constants';
import { setStatusNotification, updateStatusNotification, deleteStatusNotification } from '../redux/actions';

export const useBeamOperations = (deviceId) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [kpiMetrics, setKpiMetrics] = useState(null);
  const dispatch = useDispatch();

  const setCustomBeam = useCallback(
    async ({ azimuth, elevation }, acquisitionInfo) => {
      setIsLoading(true);
      setError(null);
      setKpiMetrics(null);

      // Notify BeamSelectionForm that API is in progress
      window.dispatchEvent(new CustomEvent('beamApiStatus', { 
        detail: { inProgress: true } 
      }));

      // Set pending status notification
      dispatch(setStatusNotification({ 
        id: 'beam', 
        text: 'Getting beam KPIs...', 
        status: Status.PENDING 
      }));

      try {
        const metrics = await getBeamKPIs({
          deviceId,
          beamParams: { azimuth, elevation },
          acquisitionInfo
        });

        // Set success status notification
        dispatch(updateStatusNotification({ 
          id: 'beam', 
          text: 'Beam KPIs retrieved successfully', 
          status: Status.SUCCESS 
        }));

        // Auto-close the notification after 3 seconds
        setTimeout(() => {
          dispatch(deleteStatusNotification({ id: 'beam' }));
        }, 3000);

        setKpiMetrics(metrics);
        
        // Notify BeamSelectionForm that API is complete
        window.dispatchEvent(new CustomEvent('beamApiStatus', { 
          detail: { inProgress: false } 
        }));
        
        return metrics;
      } catch (err) {
        // Set error status notification
        dispatch(updateStatusNotification({ 
          id: 'beam', 
          text: 'Failed to get beam KPIs', 
          status: Status.FAILURE 
        }));

        // Auto-close the error notification after 3 seconds
        setTimeout(() => {
          dispatch(deleteStatusNotification({ id: 'beam' }));
        }, 3000);

        const errorMessage = err instanceof BeamError 
          ? BEAM_ERROR_MESSAGES[err.code] || BEAM_ERROR_MESSAGES.DEFAULT
          : BEAM_ERROR_MESSAGES.DEFAULT;

        setError({ message: errorMessage, code: err.code });
        
        // Notify BeamSelectionForm that API is complete (even though it failed)
        window.dispatchEvent(new CustomEvent('beamApiStatus', { 
          detail: { inProgress: false } 
        }));
        
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [deviceId, dispatch]
  );

  const cancelCustomBeam = useCallback(() => {
    setError(null);
    setIsLoading(false);
    setKpiMetrics(null);
    
    // Clear any existing status notification
    dispatch(deleteStatusNotification({ id: 'beam' }));
    
    // Notify BeamSelectionForm that API is complete
    window.dispatchEvent(new CustomEvent('beamApiStatus', { 
      detail: { inProgress: false } 
    }));
  }, [dispatch]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    setCustomBeam,
    cancelCustomBeam,
    isLoading,
    error,
    kpiMetrics,
    clearError
  };
};
